import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form } from 'react-bootstrap';
import axios from 'axios';
import { BASE_URL } from '../App';
import '../styles/hiddenMode.css';

// API URLs for different modes
const NORMAL_MODE_API = 'https://api.mr-mohammedsaad.com';
const HIDDEN_MODE_API = 'https://api.mr-mohammedsaad.com';

function HiddenMode() {
  const [currentMode, setCurrentMode] = useState('normal');
  const [transactions, setTransactions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCurrentMode();
    fetchTransactions();
  }, []);

  const fetchCurrentMode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${NORMAL_MODE_API}/api/hidden-build/current-mode`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      // Handle the actual API response structure
      if (response.data.status === 'success') {
        const mode = response.data.data.currentMode === 0 ? 'normal' : 'hidden';
        setCurrentMode(mode);
      }
    } catch (error) {
      console.error('Error fetching current mode:', error);
    }
  };

  const switchMode = async (newMode) => {
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${NORMAL_MODE_API}/api/hidden-build/switch-mode`, 
        { currentMode: newMode === 'hidden' ? 1 : 0 },
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (response.data.status === 'success') {
        setCurrentMode(newMode);
        // Refresh transactions after mode switch
        fetchTransactions();
      }
    } catch (error) {
      console.error('Error switching mode:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      // Use the endpoint from Postman collection
      const response = await axios.get(`${NORMAL_MODE_API}/api/hidden-build`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.data.status === 'success') {
        const data = response.data.data.transactions || [];
        const selectedDate = response.data.data.selectedDate;
        const totalPaid = response.data.data.totalPaid;
        
        setTransactions(data);
        setFilteredTransactions(data);
        setSelectedDate(selectedDate);
        setTotalPaid(totalPaid);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('حدث خطأ في تحميل البيانات');
      setIsLoading(false);
    }
  };

  const filterTransactions = async () => {
    // Use the new function that follows Postman collection endpoints
    await getTransactionsByDate(selectedDate);
  };

  const getTransactionsByDate = async (date) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      // Use the endpoint from Postman collection for specific date
      const response = await axios.get(`${NORMAL_MODE_API}/api/hidden-build?date=${date}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.data.status === 'success') {
        const data = response.data.data.transactions || [];
        const selectedDate = response.data.data.selectedDate;
        const totalPaid = response.data.data.totalPaid;
        
        setFilteredTransactions(data);
        setSelectedDate(selectedDate);
        setTotalPaid(totalPaid);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching transactions by date:', error);
      setError('حدث خطأ في تحميل البيانات للتاريخ المحدد');
      setIsLoading(false);
    }
  };

  const resetFilter = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setFilteredTransactions(transactions);
    // Recalculate total paid from current transactions
    const total = transactions
      .filter(t => t.status === 'completed' || t.status === 'PAID')
      .reduce((sum, t) => sum + parseFloat(t.total || 0), 0);
    setTotalPaid(total);
  };



  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
        numberingSystem: 'arab'
      };
      const formatter = new Intl.DateTimeFormat('ar-EG', options);
      return formatter.format(date);
    } catch (e) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'paid':
        return <Badge bg="success">مكتمل</Badge>;
      case 'pending':
        return <Badge bg="warning">قيد الانتظار</Badge>;
      case 'failed':
        return <Badge bg="danger">فشل</Badge>;
      case 'cancelled':
        return <Badge bg="secondary">ملغي</Badge>;
      default:
        return <Badge bg="info">{status || 'غير محدد'}</Badge>;
    }
  };

  if (isLoading && transactions.length === 0) {
    return (
      <div className="hidden-mode-loading">
        <div className="loading-content">
          <h2>جاري تحميل بيانات المعاملات...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="hidden-mode-page">
      <Container>
        <Row>
          <Col lg={12}>
            {/* Transactions Mode Card */}
            <Card className="mode-card">
              <Card.Body>
                <div className="mode-header">
                  <h3>
                    <i className="fas fa-credit-card me-2"></i>
                    Transactions Mode
                  </h3>
                  <div className="mode-buttons">
                    <Button
                      variant={currentMode === 'hidden' ? 'danger' : 'outline-danger'}
                      size="lg"
                      onClick={() => switchMode('hidden')}
                      disabled={isSubmitting}
                      className="mode-btn"
                    >
                      <i className="fas fa-eye-slash me-2"></i>
                      Hide Mode
                    </Button>
                    <Button
                      variant={currentMode === 'normal' ? 'success' : 'outline-success'}
                      size="lg"
                      onClick={() => switchMode('normal')}
                      disabled={isSubmitting}
                      className="mode-btn"
                    >
                      <i className="fas fa-eye me-2"></i>
                      Normal Mode
                    </Button>
                  </div>
                  <div className="current-mode-indicator">
                    <Button
                      variant={currentMode === 'hidden' ? 'danger' : 'success'}
                      size="sm"
                      disabled
                    >
                      {currentMode === 'hidden' ? 'Hidden Mode' : 'Normal Mode'}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Date Selection and Filtering */}
            <Card className="filter-card">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <i className="fas fa-calendar me-2"></i>
                        Select Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="date-input"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="d-flex align-items-end">
                    <div className="filter-buttons">
                      <Button
                        variant="primary"
                        onClick={filterTransactions}
                        disabled={isLoading}
                        className="me-2"
                      >
                        <i className="fas fa-search me-2"></i>
                        Filter
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={resetFilter}
                        disabled={isLoading}
                      >
                        <i className="fas fa-redo me-2"></i>
                        Reset
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Total Paid Banner */}
            <div className="total-paid-banner">
              <i className="fas fa-coins me-2"></i>
              Total Paid for {selectedDate}: {totalPaid.toFixed(2)}
            </div>

            {/* Filter by Status */}
            <Card className="status-filter-card">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Filter by Status:</Form.Label>
                      <Form.Select className="status-select">
                        <option value="all">All</option>
                        <option value="completed">Completed</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Transactions Table */}
            <Card className="transactions-card">
              <Card.Body>
                {filteredTransactions.length === 0 ? (
                  <div className="no-transactions">
                    <h3>لا توجد معاملات للتاريخ المحدد</h3>
                    <p>لم يتم العثور على أي معاملات للتاريخ {selectedDate}</p>
                  </div>
                ) : (
                  <div className="transactions-table-container">
                    <Table responsive striped hover className="transactions-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Merchant Ref</th>
                          <th>Total</th>
                          <th>Status</th>
                          <th>Method</th>
                          <th>Paid At</th>
                          <th>Course Id</th>
                          <th>Course Name</th>
                          <th>User ID</th>
                          <th>User Name</th>
                          <th>Mobile</th>
                          <th>Email</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                                               <tbody>
                           {filteredTransactions.map((transaction) => (
                             <tr key={transaction.id}>
                               <td>
                                 <strong>#{transaction.id}</strong>
                               </td>
                               <td>{transaction.merchantRefNum || '-'}</td>
                               <td>
                                 <span className="amount">
                                   {transaction.total} جنيه
                                 </span>
                               </td>
                               <td>{getStatusBadge(transaction.status)}</td>
                               <td>
                                 <span className="payment-method">
                                   {transaction.payment_method || '-'}
                                 </span>
                               </td>
                               <td>
                                 {transaction.paid_at ? formatDate(transaction.paid_at) : '-'}
                               </td>
                               <td>{transaction.course_id || '-'}</td>
                               <td>{transaction.course?.name || '-'}</td>
                               <td>{transaction.user_id || '-'}</td>
                               <td>
                                 {transaction.customer_name || `${transaction.user?.fname || ''} ${transaction.user?.lname || ''}`.trim() || '-'}
                               </td>
                               <td>
                                 {transaction.customer_mobile || transaction.user?.phone || '-'}
                               </td>
                               <td>
                                 {transaction.customer_email || transaction.user?.email || '-'}
                               </td>
                               <td>
                                 <span className="created-date">
                                   {formatDate(transaction.created_at)}
                                 </span>
                               </td>
                             </tr>
                           ))}
                         </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default HiddenMode;
